import { memo, useCallback, useEffect, useRef, useState } from "react";
import { Text } from "src/components/elements/typography/Text";
import styles from "src/components/features/legal/styles.module.scss";

type LicenseInfo = {
  licenses: string;
  repository?: string;
  name: string;
  copyright?: string;
};

export const UserSoftwareLicenseTemplate = memo(() => {
  const [licenseData, setLicenseData] = useState<{
    [key: string]: LicenseInfo;
  }>({});
  const [displayCount, setDisplayCount] = useState(20);
  const lastItemRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    fetch("/license/license-package.json")
      .then((response) => response.json())
      .then((data) => setLicenseData(data));
  }, []);

  const handleLoadMore = useCallback(() => {
    setDisplayCount((prevCount) => prevCount + 20);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (
          entry.isIntersecting &&
          Object.keys(licenseData).length > displayCount
        ) {
          handleLoadMore();
        }
      },
      { threshold: 1.0 }
    );

    const currentLastItem = lastItemRef.current;
    if (currentLastItem) {
      observer.observe(currentLastItem);
    }

    return () => {
      if (currentLastItem) {
        observer.unobserve(currentLastItem);
      }
    };
  }, [licenseData, displayCount, handleLoadMore]);

  return (
    <div>
      {Object.entries(licenseData)
        .slice(0, displayCount)
        .map(([packageName, packageInfo]) => (
          <div key={packageName}>
            <div className={styles.licenseDataContainer}>
              <Text className={styles.licenseMainText}>{packageInfo.name}</Text>
              <Text className={styles.licenseSubText}>
                license: {packageInfo.licenses}
              </Text>
              {packageInfo.copyright && (
                <Text className={styles.licenseSubText}>
                  {packageInfo.copyright}
                </Text>
              )}
              {packageInfo.repository && (
                <Text className={styles.licenseSubText}>
                  <a
                    className={styles.licenseLink}
                    href={packageInfo.repository}
                  >
                    {packageInfo.repository}
                  </a>
                </Text>
              )}
            </div>
          </div>
        ))}
      <div ref={lastItemRef} />
    </div>
  );
});
